import type { FlowAction, FlowNode, FlowState, LearningFlow } from "./flow";

export function findFlowNode(flow: LearningFlow, nodeId: string): FlowNode | undefined {
  return flow.nodes.find((node) => node.id === nodeId);
}

export function validateFlow(flow: LearningFlow): readonly string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const node of flow.nodes) {
    if (ids.has(node.id)) errors.push(`duplicate_node:${node.id}`);
    ids.add(node.id);
  }
  if (!ids.has(flow.startNodeId)) errors.push(`missing_start:${flow.startNodeId}`);
  for (const node of flow.nodes) {
    if (node.next && !ids.has(node.next)) errors.push(`missing_target:${node.id}:${node.next}`);
    for (const choice of node.choices ?? []) if (!ids.has(choice.target)) errors.push(`missing_choice_target:${node.id}:${choice.id}:${choice.target}`);
    if (node.kind === "question" && !node.choices?.length) errors.push(`question_without_choices:${node.id}`);
  }
  return errors;
}

export function createFlowState(flow: LearningFlow, requestedNodeId?: string): FlowState {
  const currentNodeId = requestedNodeId && findFlowNode(flow, requestedNodeId) ? requestedNodeId : flow.startNodeId;
  return { currentNodeId, history: [], selections: {}, completed: findFlowNode(flow, currentNodeId)?.kind === "summary" };
}

function move(state: FlowState, target: string, completed = false): FlowState {
  return { ...state, currentNodeId: target, history: [...state.history, state.currentNodeId], completed };
}

export function reduceFlow(flow: LearningFlow, state: FlowState, action: FlowAction): FlowState {
  if (action.type === "restart") return createFlowState(flow);
  if (action.type === "open") return findFlowNode(flow, action.nodeId) ? move(state, action.nodeId, findFlowNode(flow, action.nodeId)?.kind === "summary") : state;
  if (action.type === "back") {
    const target = state.history.at(-1);
    return target ? { ...state, currentNodeId: target, history: state.history.slice(0, -1), completed: false } : state;
  }
  const node = findFlowNode(flow, state.currentNodeId);
  if (!node) return createFlowState(flow);
  if (action.type === "continue") return node.next ? move(state, node.next, findFlowNode(flow, node.next)?.kind === "summary") : state;
  const choice = node.choices?.find((item) => item.id === action.choiceId);
  return choice ? move({ ...state, selections: { ...state.selections, [node.id]: choice.id } }, choice.target, findFlowNode(flow, choice.target)?.kind === "summary") : state;
}
